/**
 * Superadmin: SEO health — first-party traffic + crawl/sitemap checks (geen Google-producten).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Globe,
  LayoutList,
  LineChart,
  RefreshCw,
  Search,
  Server,
} from 'lucide-react';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { SeoMeta } from '../../components/SeoMeta';
import { api } from '../../lib/api';

const DAY_OPTIONS = [7, 30, 90] as const;

export function AdminSeoHealth() {
  const [days, setDays] = useState<(typeof DAY_OPTIONS)[number]>(30);
  const [siteOrigin, setSiteOrigin] = useState('');
  const [maxChecks, setMaxChecks] = useState(40);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin-seo-health', days, siteOrigin || null, maxChecks],
    queryFn: () =>
      api.getAdminSeoHealth({
        days,
        ...(siteOrigin.trim() ? { site_origin: siteOrigin.trim() } : {}),
        max_url_checks: maxChecks,
      }),
  });

  const errMsg = error instanceof Error ? error.message : String(error ?? '');
  const isForbidden = errMsg.toLowerCase().includes('forbidden') || errMsg.toLowerCase().includes('superadmin');

  const okSamples = data?.indexing_and_crawl.url_samples.filter((u) => u.ok).length ?? 0;
  const badSamples = (data?.indexing_and_crawl.url_samples.length ?? 0) - okSamples;

  return (
    <div>
      <SeoMeta
        path="/admin/seo-health"
        noindex
        title="SEO health"
        description="SEO health dashboard: first-party traffic-proxy, robots/sitemap-checks en URL-steekproef — superadmin; geen Google-productdata."
      />
      <Breadcrumbs items={[{ label: 'Platformbeheer', href: '/admin' }, { label: 'SEO health' }]} />
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-2">
          <Search className="h-6 w-6 text-primary" aria-hidden />
          <h1 className="text-2xl font-bold text-text">SEO health</h1>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label htmlFor="seo-days" className="mb-1 block text-xs text-text-muted">
              Periode (analytics)
            </label>
            <select
              id="seo-days"
              value={days}
              onChange={(e) => setDays(Number(e.target.value) as (typeof DAY_OPTIONS)[number])}
              className="rounded-lg border border-border bg-surface-card px-3 py-2 text-sm text-text"
            >
              {DAY_OPTIONS.map((d) => (
                <option key={d} value={d}>
                  {d} dagen
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="seo-origin" className="mb-1 block text-xs text-text-muted">
              Site-origin (optioneel)
            </label>
            <input
              id="seo-origin"
              type="url"
              placeholder="https://www.calqtool.com"
              value={siteOrigin}
              onChange={(e) => setSiteOrigin(e.target.value)}
              className="w-[min(100%,18rem)] rounded-lg border border-border bg-surface-card px-3 py-2 text-sm text-text placeholder:text-text-muted"
            />
          </div>
          <div>
            <label htmlFor="seo-max" className="mb-1 block text-xs text-text-muted">
              Max URL-checks
            </label>
            <input
              id="seo-max"
              type="number"
              min={5}
              max={120}
              value={maxChecks}
              onChange={(e) => setMaxChecks(Number(e.target.value) || 40)}
              className="w-24 rounded-lg border border-border bg-surface-card px-3 py-2 text-sm text-text"
            />
          </div>
          <button
            type="button"
            onClick={() => void refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface-card px-3 py-2 text-sm font-medium text-text hover:bg-surface disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} aria-hidden />
            Vernieuwen
          </button>
        </div>
      </div>

      <p className="mb-6 max-w-3xl text-sm text-text-muted">
        Dit dashboard benadert Google Analytics (verkeer op je eigen domein) en Search Console (landingspagina’s uit
        zoek-referrer + HTTP-status uit sitemap) <strong className="font-medium text-text">zonder Google-producten</strong>.
        Voor echte impressies en indexdekking blijft externe Search Console het nauwst bij Google.
      </p>

      {isForbidden && (
        <div className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-text">
          Geen toegang. Alleen Platformbeheer (rol superadmin).
        </div>
      )}

      {errMsg && !isForbidden && (
        <div className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-text">
          {errMsg}
        </div>
      )}

      {isLoading && <p className="text-text-muted">Laden…</p>}

      {data && (
        <div className="space-y-8">
          <section className="rounded-xl border border-border bg-surface-card p-5">
            <div className="mb-4 flex items-center gap-2">
              <Globe className="h-5 w-5 text-primary" aria-hidden />
              <h2 className="text-lg font-semibold text-text">Site &amp; bron</h2>
            </div>
            <dl className="grid gap-2 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-text-muted">Gecontroleerde origin</dt>
                <dd className="font-mono text-text">{data.site_origin}</dd>
              </div>
              <div>
                <dt className="text-text-muted">Bron origin</dt>
                <dd className="text-text">{data.site_origin_source}</dd>
              </div>
              <div>
                <dt className="text-text-muted">Snapshot</dt>
                <dd className="text-text">{new Date(data.generated_at).toLocaleString('nl-NL')}</dd>
              </div>
            </dl>
          </section>

          <section className="rounded-xl border border-border bg-surface-card p-5">
            <div className="mb-4 flex items-center gap-2">
              <LineChart className="h-5 w-5 text-primary" aria-hidden />
              <h2 className="text-lg font-semibold text-text">Verkeer (first-party, alle tenants)</h2>
            </div>
            <p className="mb-4 text-xs text-text-muted">
              Vergelijkbaar met hoog-niveau GA: page views en sessies uit eigen{' '}
              <code className="rounded bg-surface px-1">analytics_events</code>.
            </p>
            <div className="mb-6 grid gap-4 sm:grid-cols-2">
              <div className="rounded-lg border border-border bg-surface p-4">
                <p className="text-xs text-text-muted">Page views</p>
                <p className="text-2xl font-semibold text-text">{data.traffic.page_views_total}</p>
              </div>
              <div className="rounded-lg border border-border bg-surface p-4">
                <p className="text-xs text-text-muted">Unieke sessies (schatting)</p>
                <p className="text-2xl font-semibold text-text">{data.traffic.unique_sessions_estimate}</p>
              </div>
            </div>
            <h3 className="mb-2 text-sm font-medium text-text">Kanaal (referrer)</h3>
            <div className="mb-6 overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-border text-text-muted">
                    <th className="py-2 pr-4">Kanaal</th>
                    <th className="py-2">Views</th>
                  </tr>
                </thead>
                <tbody>
                  {data.traffic.channel_breakdown.map((c) => (
                    <tr key={c.channel} className="border-b border-border/60">
                      <td className="py-2 pr-4 text-text">{c.label_nl}</td>
                      <td className="py-2 text-text">{c.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <h3 className="mb-2 text-sm font-medium text-text">Top marketing-paden</h3>
            <ul className="mb-4 space-y-1 font-mono text-xs text-text">
              {data.traffic.top_marketing_paths.slice(0, 15).map((p) => (
                <li key={p.path}>
                  {p.path} <span className="text-text-muted">({p.count})</span>
                </li>
              ))}
            </ul>
            <h3 className="mb-2 text-sm font-medium text-text">Top referrers (ruw)</h3>
            <ul className="max-h-40 space-y-1 overflow-y-auto font-mono text-[11px] text-text-muted">
              {data.traffic.top_referrers.map((r) => (
                <li key={r.referrer} className="truncate" title={r.referrer}>
                  {r.referrer} <span className="text-text">({r.count})</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="rounded-xl border border-border bg-surface-card p-5">
            <div className="mb-2 flex items-center gap-2">
              <LayoutList className="h-5 w-5 text-primary" aria-hidden />
              <h2 className="text-lg font-semibold text-text">Zoek-prestaties (proxy)</h2>
            </div>
            <p className="mb-4 text-sm text-amber-800 dark:text-amber-200/90">{data.search_performance_proxy.disclaimer_nl}</p>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-border text-text-muted">
                    <th className="py-2 pr-4">Pad</th>
                    <th className="py-2 pr-4">“Klikken” (proxy)</th>
                    <th className="py-2">Toelichting</th>
                  </tr>
                </thead>
                <tbody>
                  {data.search_performance_proxy.rows.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="py-4 text-text-muted">
                        Nog geen page views met zoek-referrer in deze periode.
                      </td>
                    </tr>
                  ) : (
                    data.search_performance_proxy.rows.slice(0, 25).map((r) => (
                      <tr key={r.path} className="border-b border-border/60">
                        <td className="py-2 pr-4 font-mono text-xs text-text">{r.path}</td>
                        <td className="py-2 pr-4 text-text">{r.clicks_proxy}</td>
                        <td className="py-2 text-xs text-text-muted">{r.note}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-xl border border-border bg-surface-card p-5">
            <div className="mb-2 flex items-center gap-2">
              <Server className="h-5 w-5 text-primary" aria-hidden />
              <h2 className="text-lg font-semibold text-text">Indexering &amp; crawl (HTTP)</h2>
            </div>
            <p className="mb-4 text-sm text-text-muted">{data.indexing_and_crawl.disclaimer_nl}</p>

            <div className="mb-6 rounded-lg border border-border bg-surface p-4">
              <h3 className="mb-2 text-sm font-medium text-text">robots.txt</h3>
              <div className="mb-2 flex flex-wrap items-center gap-2">
                {data.indexing_and_crawl.robots.ok ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-amber-600" aria-hidden />
                )}
                <span className="font-mono text-xs text-text">{data.indexing_and_crawl.robots.url}</span>
                <span className="text-xs text-text-muted">HTTP {data.indexing_and_crawl.robots.status || '—'}</span>
                {data.indexing_and_crawl.robots.mentions_sitemap && (
                  <span className="rounded bg-primary/10 px-2 py-0.5 text-xs text-primary">verwijst naar sitemap</span>
                )}
              </div>
              {data.indexing_and_crawl.robots.snippet && (
                <pre className="max-h-32 overflow-auto rounded bg-surface-card p-2 font-mono text-[11px] text-text-muted">
                  {data.indexing_and_crawl.robots.snippet}
                </pre>
              )}
            </div>

            <div className="mb-6">
              <h3 className="mb-2 text-sm font-medium text-text">Sitemaps</h3>
              <ul className="space-y-2">
                {data.indexing_and_crawl.sitemaps.map((s) => (
                  <li
                    key={s.url}
                    className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2 text-sm"
                  >
                    {s.ok ? (
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
                    ) : (
                      <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" aria-hidden />
                    )}
                    <span className="font-mono text-xs text-text">{s.url}</span>
                    <span className="text-xs text-text-muted">
                      {s.status || '—'} · {s.url_count_in_file} locs {s.is_index ? '(index)' : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-6 rounded-lg border border-border bg-surface p-4">
              <h3 className="mb-2 text-sm font-medium text-text">Homepage SEO-tags</h3>
              <p className="mb-2 font-mono text-xs text-text-muted">{data.indexing_and_crawl.homepage_signals.url}</p>
              <ul className="flex flex-wrap gap-3 text-sm">
                <li className="flex items-center gap-1">
                  {data.indexing_and_crawl.homepage_signals.has_title ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                  )}
                  &lt;title&gt;
                </li>
                <li className="flex items-center gap-1">
                  {data.indexing_and_crawl.homepage_signals.has_meta_description ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                  )}
                  meta description
                </li>
              </ul>
              {data.indexing_and_crawl.homepage_signals.title_preview && (
                <p className="mt-2 text-xs text-text-muted">
                  Titel: {data.indexing_and_crawl.homepage_signals.title_preview}
                </p>
              )}
            </div>

            <div>
              <h3 className="mb-2 text-sm font-medium text-text">
                URL-steekproef uit sitemap ({okSamples} ok, {badSamples} fout)
              </h3>
              <div className="max-h-64 overflow-y-auto rounded-lg border border-border">
                <table className="w-full text-left text-xs">
                  <thead className="sticky top-0 bg-surface-card">
                    <tr className="border-b border-border text-text-muted">
                      <th className="p-2">URL</th>
                      <th className="p-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.indexing_and_crawl.url_samples.map((u) => (
                      <tr key={u.url} className="border-b border-border/50">
                        <td className="p-2 font-mono text-[10px] text-text break-all">{u.url}</td>
                        <td className="p-2">
                          {u.ok ? (
                            <span className="text-emerald-600">{u.status}</span>
                          ) : (
                            <span className="text-amber-700">{u.status || 'fail'}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <section className="rounded-xl border border-border bg-surface-card p-5">
            <div className="mb-2 flex items-center gap-2">
              <ExternalLink className="h-5 w-5 text-primary" aria-hidden />
              <h2 className="text-lg font-semibold text-text">Extern (dichtst bij Google)</h2>
            </div>
            <p className="mb-4 text-sm text-text-muted">{data.external.note_nl}</p>
            <div className="flex flex-wrap gap-3">
              <a
                href={data.external.google_search_console_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface px-4 py-2 text-sm font-medium text-primary hover:bg-surface-card"
              >
                Google Search Console
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
              </a>
              <a
                href={data.external.bing_webmaster_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface px-4 py-2 text-sm font-medium text-primary hover:bg-surface-card"
              >
                Bing Webmaster Tools
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
              </a>
            </div>
            <p className="mt-4 text-xs text-text-muted">
              CLI (CI): <code className="rounded bg-surface px-1">pnpm seo:health https://www.calqtool.com</code>
            </p>
          </section>
        </div>
      )}
    </div>
  );
}
